import React, { useState, useRef, useEffect } from 'react';
import { Menu, X, ChevronDown, Globe, User, LogOut, LayoutDashboard } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes.constants';
import Button from '../common/Button';
import Logo from '../common/Logo';
import NotificationBell from '../common/NotificationBell';
import { useLanguage } from '../../context/LanguageContext';

/** Lien dashboard selon le rôle (admin panel / vendor uniquement). */
const getDashboardLink = (role) => {
  if (role === 'super_admin' || role === 'admin' || role === 'staff') {
    return {
      path: ROUTES.ADMIN_DASHBOARD,
      labelKey: 'navbar.dashboardAdmin',
      fallback: 'Espace Admin',
    };
  }
  if (role === 'vendor') {
    return {
      path: ROUTES.VENDOR_DASHBOARD,
      labelKey: 'navbar.dashboardVendor',
      fallback: 'Tableau de bord',
    };
  }
  return null;
};

const Navbar = ({ isLoggedIn, user, onLogout }) => {
  const { t, currentLanguage, languages, changeLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const langRef = useRef(null);
  const userMenuRef = useRef(null);

  const isAdminPage = location.pathname.startsWith('/admin');
  const isDashboardPage =
    location.pathname.startsWith('/vendor/') || location.pathname.startsWith('/user/dashboard');
  const isFullWidth = isAdminPage || isDashboardPage;

  const dashboardLink = isLoggedIn ? getDashboardLink(user?.role) : null;
  const showNotifications =
    isLoggedIn &&
    (user?.role === 'admin' ||
      user?.role === 'super_admin' ||
      user?.role === 'staff' ||
      user?.role === 'vendor');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setIsLangOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="bg-white/95 backdrop-blur-md sticky top-0 z-50 border-b border-gray-100">
      <div
        className={`${isFullWidth ? 'w-full' : 'container mx-auto'} px-4 h-16 flex justify-between items-center`}
      >
        <Link
          to={ROUTES.HOME}
          className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
        >
          <Logo size="text-3xl" carColor="text-[#27489E]" vyColor="text-[#F5A623]" />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link
            to={ROUTES.HOME}
            className={`font-medium transition ${
              location.pathname === ROUTES.HOME
                ? 'text-[#27489E]'
                : 'text-gray-600 hover:text-[#27489E]'
            }`}
          >
            {t('navbar.home')}
          </Link>
          <Link
            to={ROUTES.VEHICLES}
            className={`font-medium transition ${
              location.pathname === ROUTES.VEHICLES
                ? 'text-[#27489E]'
                : 'text-gray-600 hover:text-[#27489E]'
            }`}
          >
            {t('navbar.vehicles')}
          </Link>
          <Link
            to={ROUTES.BECOME_PARTNER}
            className={`font-medium transition ${
              location.pathname === ROUTES.BECOME_PARTNER
                ? 'text-[#27489E]'
                : 'text-gray-600 hover:text-[#27489E]'
            }`}
          >
            {t('navbar.agencies')}
          </Link>
          <Link
            to={ROUTES.HELP}
            className={`font-medium transition ${
              location.pathname === ROUTES.HELP
                ? 'text-[#27489E]'
                : 'text-gray-600 hover:text-[#27489E]'
            }`}
          >
            {t('navbar.help')}
          </Link>
        </div>

        <div className="flex items-center gap-3">
          {showNotifications && <NotificationBell userRole={user?.role} />}

          <div className="relative hidden md:block" ref={langRef}>
            <button
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition"
            >
              <img
                src={currentLanguage.flag}
                alt={currentLanguage.label}
                className="w-5 h-auto rounded-sm"
              />
              <span>{currentLanguage.code.toUpperCase()}</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${isLangOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {isLangOpen && (
              <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 py-2 min-w-[160px] z-50 animate-in fade-in slide-in-from-top-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsLangOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition ${
                      currentLanguage.code === lang.code
                        ? 'bg-[#27489E]/5 text-[#27489E]'
                        : 'text-gray-700'
                    }`}
                  >
                    <img src={lang.flag} alt={lang.label} className="w-5 h-auto rounded-sm" />
                    <span className="font-medium">{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Button
            variant="ghost"
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X /> : <Menu />}
          </Button>

          {isLoggedIn ? (
            <div className="relative hidden md:block" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-2 px-3 py-1.5 bg-[#27489E]/10 rounded-full border border-[#27489E]/20 text-sm font-semibold text-[#27489E] cursor-pointer hover:bg-[#27489E]/20 transition"
              >
                <div className="w-7 h-7 bg-[#27489E] rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <span className="max-w-[100px] truncate">
                  {user?.fullName || user?.email || 'Mon compte'}
                </span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isUserMenuOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 py-2 min-w-[200px] z-50 animate-in fade-in slide-in-from-top-2">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="font-semibold text-gray-800 truncate">
                      {user?.fullName || 'Utilisateur'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
                  </div>
                  {dashboardLink && (
                    <Link
                      to={dashboardLink.path}
                      onClick={() => setIsUserMenuOpen(false)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-50 transition"
                    >
                      <LayoutDashboard className="w-4 h-4 text-[#27489E]" />
                      <span className="font-medium">
                        {t(dashboardLink.labelKey) || dashboardLink.fallback}
                      </span>
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      onLogout();
                      setIsUserMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-red-600 hover:bg-red-50 transition"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="font-medium">{t('navbar.logout')}</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Button
                variant="secondary"
                className="hidden md:flex"
                onClick={() => navigate(ROUTES.LOGIN)}
              >
                {t('navbar.login')}
              </Button>
              <Button
                variant="primary"
                className="hidden md:flex"
                onClick={() => navigate(ROUTES.REGISTER)}
              >
                {t('navbar.signup')}
              </Button>
            </>
          )}
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t p-4 flex flex-col gap-4 animate-in slide-in-from-top-2">
          <Link to={ROUTES.HOME} onClick={() => setIsMobileMenuOpen(false)}>
            <Button variant="ghost" className="justify-start w-full">
              {t('navbar.home')}
            </Button>
          </Link>
          <Link to={ROUTES.VEHICLES} onClick={() => setIsMobileMenuOpen(false)}>
            <Button variant="ghost" className="justify-start w-full">
              {t('navbar.vehicles')}
            </Button>
          </Link>
          <Link to={ROUTES.BECOME_PARTNER} onClick={() => setIsMobileMenuOpen(false)}>
            <Button variant="ghost" className="justify-start w-full">
              {t('navbar.agencies')}
            </Button>
          </Link>
          <div className="h-px bg-gray-100 my-2" />

          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
            <Globe className="w-4 h-4" />
            <span>{t('navbar.language')}</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition ${
                  currentLanguage.code === lang.code
                    ? 'border-[#27489E] bg-[#27489E]/5 text-[#27489E]'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <img src={lang.flag} alt={lang.label} className="w-5 h-auto rounded-sm" />
                <span className="font-medium text-sm">{lang.label}</span>
              </button>
            ))}
          </div>

          <div className="h-px bg-gray-100 my-2" />
          {isLoggedIn ? (
            <>
              <div className="flex items-center gap-3 px-2 py-2 mb-2">
                <div className="w-10 h-10 bg-[#27489E] rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{user?.fullName || 'Utilisateur'}</p>
                  <p className="text-xs text-gray-500">{user?.email || ''}</p>
                </div>
              </div>
              {dashboardLink && (
                <Button
                  variant="ghost"
                  className="w-full justify-start text-[#27489E] hover:bg-[#27489E]/5"
                  onClick={() => {
                    navigate(dashboardLink.path);
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  {t(dashboardLink.labelKey) || dashboardLink.fallback}
                </Button>
              )}
              <Button
                variant="ghost"
                className="w-full justify-start text-red-600 hover:bg-red-50"
                onClick={() => {
                  onLogout();
                  setIsMobileMenuOpen(false);
                }}
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('navbar.logout')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                className="w-full justify-start mb-2"
                onClick={() => {
                  navigate(ROUTES.LOGIN);
                  setIsMobileMenuOpen(false);
                }}
              >
                {t('navbar.login')}
              </Button>
              <Button
                variant="primary"
                className="w-full"
                onClick={() => {
                  navigate(ROUTES.REGISTER);
                  setIsMobileMenuOpen(false);
                }}
              >
                {t('navbar.signup')}
              </Button>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
